import { Operation, Persistence } from './persistence';
import { State } from './state';
import { Action } from './process/action';
import { Task } from './process/task';
import { WeblabService } from '../../src/services/WeblabService';
import { modelRegistrySingleton } from '@amzn/ihm-js-coral-client';

type Base = Record<string, Record<string, object>>;

class ActionProxy {
    private actionId: string;
    private actionName: string;
    private expectedTaskName: string | undefined;

    constructor(action: Action) {
        if (!action?.id) {
            console.warn('Invalid action provided to ActionProxy');
            this.actionId = '';
            this.actionName = '';
            return;
        }
        this.actionId = action.id;
        this.actionName = action.actionName;
        this.expectedTaskName = action.taskName ?? undefined;
    }

    private getOrCreate(): Action | null {
        try {
            const data = Persistence.getById(Operation.ACTION, this.actionId);
            if (data) return Action.fromJSON(data);
            if (!this.actionName) return null;
            return Action.createWithId(this.actionId, this.actionName, this.expectedTaskName, false);
        } catch (e) {
            console.error(`Error in getOrCreate for action: ${e}`);
            return null;
        }
    }

    public toAction() {
        try {
            const data = Persistence.getById(Operation.ACTION, this.actionId);
            if (!data) {
                console.warn(`No data found for action ID: ${this.actionId}`);
                return {} as Action;
            }
            return Action.fromJSON(data);
        } catch (e) {
            console.error(`Error getting action data: ${e}`);
            return {} as Action;
        }
    }

    public start() {
        try {
            const action = this.getOrCreate();
            if (!action) return;
            action.start();
        } catch (e) {
            console.error(`Error starting action: ${e}`);
        }
    }

    public stop() {
        try {
            const action = this.toAction();
            action?.stop?.();
        } catch (e) {
            console.error(`Error stopping action: ${e}`);
        }
    }
}

class TaskProxy<T extends Base[keyof Base]> {
    private taskId: string;
    private actions: { [K in keyof T]: ActionProxy };

    public constructor(task: Task, actions: T) {
        if (!task?.id) {
            console.warn('Invalid task provided to TaskProxy');
            this.taskId = '';
            this.actions = {} as any;
            return;
        }
        this.taskId = task.id;
        this.actions = {} as any;
        try {
            for (const actionName in actions) {
                if (!actions.hasOwnProperty(actionName)) {
                    continue;
                }
                const action = task.addAction(actionName);
                if (action) {
                    this.actions[actionName] = new ActionProxy(action);
                }
            }
        } catch (e) {
            console.error(`Error initializing actions: ${e}`);
        }
    }

    public toTask(): Task {
        try {
            const data = Persistence.getById(Operation.TASK, this.taskId);
            if (!data) {
                console.warn(`No data found for task ID: ${this.taskId}`);
                return {} as Task;
            }
            return Task.fromJSON(data);
        } catch (e) {
            console.error(`Error getting task data: ${e}`);
            return {} as Task;
        }
    }

    public start() {
        try {
            const task = this.toTask();
            task?.start?.();
        } catch (e) {
            console.error(`Error starting task: ${e}`);
        }
    }

    public stop() {
        try {
            const task = this.toTask();
            task?.stop?.();
        } catch (e) {
            console.error(`Error stopping task: ${e}`);
        }
    }

    public get<K extends keyof T>(actionName: K): ActionProxy {
        return this.actions[actionName] || new NullActionProxy();
    }
}

class NullActionProxy extends ActionProxy {
    constructor() {
        super({ id: '' } as Action);
    }
    public toAction(): Action {
        return {} as Action;
    }
    public start(): void {
        return;
    }
    public stop(): void {
        return;
    }
}

class NullTaskProxy<T extends Base[keyof Base]> extends TaskProxy<T> {
    constructor() {
        super({ id: '' } as Task, {} as T);
    }
    public toTask(): Task {
        return {} as Task;
    }
    public start(): void {
        return;
    }
    public stop(): void {
        return;
    }
    public get<K extends keyof T>(actionName: K): ActionProxy {
        return new NullActionProxy();
    }
}
export class Model<T extends Base> {
    private static instance: Model<any>;
    private tasks: { [K in keyof T]: TaskProxy<T[K]> };
    private initialStruct: T;

    public constructor(structure: T, telemEnabled: boolean = true) {
        if (Model.instance) {
            return Model.instance;
        }
        this.initialStruct = structure;
        this.tasks = {} as any;
        if (telemEnabled) this.init(structure);
        Model.instance = this;
    }

    private init(structure: T): void {
        try {
            const workflow = State.getWorkflow();
            if (!workflow) {
                console.warn('Process Model: No workflow found, using null implementation.');
                return;
            }

            for (const taskName in structure) {
                if (Object.prototype.hasOwnProperty.call(structure, taskName)) {
                    const task = workflow.addTask(taskName);
                    if (task) {
                        this.tasks[taskName] = new TaskProxy(task, structure[taskName]);
                    }
                }
            }
            // Removed workflow.start() - ATBase is now responsible for starting workflows
            console.info('IhmTelemetry: Model initialized with workflow: ' + workflow.id);
        } catch (error) {
            console.error('Error in init:', error);
            this.tasks = {} as any;
        }
    }

    public static async checkWeblab(weblabId: string): Promise<void> {
        if (!Model.instance) {
            console.error('Model not initialized yet');
        }
        try {
            const isTelemEnabled = await WeblabService.isFeatureEnabledWithCache(weblabId);
            if (!isTelemEnabled) {
                Model.instance.tasks = {} as any;
            } else {
                Model.instance.init(Model.instance.initialStruct);
            }
        } catch (error) {
            console.error('Error checking weblab feature:', error);
            Model.instance.tasks = {} as any;
        }
    }

    public get<K extends keyof T>(taskName: K): TaskProxy<T[K]> {
        return this.tasks[taskName] || new NullTaskProxy<T[K]>();
    }
}

export function createDecorators<T extends Base>(model: Model<T>) {
    const task = <K extends keyof T>(taskName: K) => {
        return function (target: any, propertyKey: string, descriptor: PropertyDescriptor) {
            const originalMethod = descriptor.value;

            descriptor.value = function (...args: any[]) {
                model.get(taskName).start();

                try {
                    const result = originalMethod.apply(this, args);

                    if (result instanceof Promise) {
                        return result.then(
                            (value) => {
                                model.get(taskName).stop();
                                return value;
                            },
                            (error) => {
                                model.get(taskName).stop();
                                throw error;
                            },
                        );
                    }

                    model.get(taskName).stop();
                    return result;
                } catch (e) {
                    model.get(taskName).stop();
                    throw e;
                }
            };

            return descriptor;
        };
    };

    const action = <K extends keyof T, A extends keyof T[K]>(taskName: K, actionName: A) => {
        return function (target: any, propertyKey: string, descriptor: PropertyDescriptor) {
            const originalMethod = descriptor.value;

            descriptor.value = function (...args: any[]) {
                model.get(taskName).get(actionName).start();

                try {
                    const result = originalMethod.apply(this, args);

                    if (result instanceof Promise) {
                        return result.then(
                            (value) => {
                                model.get(taskName).get(actionName).stop();
                                return value;
                            },
                            (error) => {
                                model.get(taskName).get(actionName).stop();
                                throw error;
                            },
                        );
                    }

                    model.get(taskName).get(actionName).stop();
                    return result;
                } catch (e) {
                    model.get(taskName).get(actionName).stop();
                    throw e;
                }
            };

            return descriptor;
        };
    };

    return { task, action };
}

export function createWrappers<T extends Base>(model: Model<T>) {
    const task = <K extends keyof T>(taskName: K) => {
        return function <TFunc extends (...args: any[]) => any>(fn: TFunc) {
            return function (...args: Parameters<TFunc>): ReturnType<TFunc> {
                model.get(taskName).start();

                try {
                    const result = fn(...args);

                    if (result instanceof Promise) {
                        return result.then(
                            (value) => {
                                model.get(taskName).stop();
                                return value;
                            },
                            (error) => {
                                model.get(taskName).stop();
                                throw error;
                            },
                        ) as ReturnType<TFunc>;
                    }

                    model.get(taskName).stop();
                    return result;
                } catch (e) {
                    model.get(taskName).stop();
                    throw e;
                }
            };
        };
    };

    const action = <K extends keyof T, A extends keyof T[K]>(taskName: K, actionName: A) => {
        return function <TFunc extends (...args: any[]) => any>(fn: TFunc) {
            return function (...args: Parameters<TFunc>): ReturnType<TFunc> {
                model.get(taskName).get(actionName).start();

                try {
                    const result = fn(...args);

                    if (result instanceof Promise) {
                        return result.then(
                            (value) => {
                                model.get(taskName).get(actionName).stop();
                                return value;
                            },
                            (error) => {
                                model.get(taskName).get(actionName).stop();
                                throw error;
                            },
                        ) as ReturnType<TFunc>;
                    }

                    model.get(taskName).get(actionName).stop();
                    return result;
                } catch (e) {
                    model.get(taskName).get(actionName).stop();
                    throw e;
                }
            };
        };
    };

    return { task, action };
}
